import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LoteLineaButton from '../../components/lotes/LoteLineaButton';
import ordenDevolucionService, {
  RemitoPendienteDevolucion,
  RemitoPendienteDevolucionDetalle,
} from '../../services/ordenDevolucionService';
import productoService from '../../services/productoService';
import remitoVentaService, { RemitoVenta } from '../../services/remitoVentaService';
import type { LoteAsignacion } from '../../services/loteService';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatNroDocumentoVenta } from '../../utils/formatters';
import {
  extractLoteFlagsFromProducto,
  mensajeValidacionLotes,
} from '../../utils/loteLineaHelpers';

const formatCantidad = (n: number) =>
  new Intl.NumberFormat('es-AR', { maximumFractionDigits: 4, minimumFractionDigits: 0 }).format(n);

type LineaDevolucion = RemitoPendienteDevolucionDetalle & {
  selected: boolean;
  cantidadDevolver: number;
  controlLoteSerie?: string | null;
  requiereVencimiento?: boolean;
  lotes?: LoteAsignacion[];
};

const OrdenDevolucionForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const remitoIdParam = searchParams.get('remitoId');
  const remitoId = remitoIdParam ? parseInt(remitoIdParam, 10) : null;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [remito, setRemito] = useState<RemitoVenta | null>(null);
  const [lineas, setLineas] = useState<LineaDevolucion[]>([]);
  const [descripcion, setDescripcion] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerItems, setPickerItems] = useState<RemitoPendienteDevolucion[]>([]);
  const [pickerSearch, setPickerSearch] = useState('');

  useEffect(() => {
    if (!remitoId || Number.isNaN(remitoId)) return;

    const load = async () => {
      setLoading(true);
      try {
        const [remitoData, pendientes] = await Promise.all([
          remitoVentaService.getRemitoVenta(remitoId),
          ordenDevolucionService.getPendientesDeRemito(remitoId),
        ]);
        setRemito(remitoData);
        const conPendienteBase = pendientes.filter((p) => p.pendienteDevolver > 0);
        const codigos = Array.from(
          new Set(
            conPendienteBase
              .map((p) => String(p.codigoArticulo ?? '').trim())
              .filter((c) => c.length > 0),
          ),
        );
        const flagsByCodigo: Record<string, { controlLoteSerie: string; requiereVencimiento: boolean }> = {};
        await Promise.all(
          codigos.map(async (codigo) => {
            try {
              const producto = await productoService.getProducto(codigo);
              if (producto) flagsByCodigo[codigo] = extractLoteFlagsFromProducto(producto);
            } catch {
              flagsByCodigo[codigo] = { controlLoteSerie: 'N', requiereVencimiento: false };
            }
          }),
        );
        const conPendiente = conPendienteBase.map((p) => {
          const codigo = String(p.codigoArticulo ?? '').trim();
          const flags = flagsByCodigo[codigo] ?? { controlLoteSerie: 'N', requiereVencimiento: false };
          return {
            ...p,
            selected: true,
            cantidadDevolver: p.pendienteDevolver,
            controlLoteSerie: flags.controlLoteSerie,
            requiereVencimiento: flags.requiereVencimiento,
            lotes: undefined as LoteAsignacion[] | undefined,
          };
        });
        setLineas(conPendiente);
        if (conPendiente.length === 0) {
          enqueueSnackbar('El remito no tiene cantidades pendientes de devolver', { variant: 'info' });
        }
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cargar el remito'), { variant: 'error' });
        navigate('/ventas/ordenes-devolucion');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [enqueueSnackbar, navigate, remitoId]);

  useEffect(() => {
    if (remitoId) return;

    const loadPicker = async () => {
      setPickerLoading(true);
      try {
        const result = await ordenDevolucionService.getRemitosPendientes({
          pageNumber: 1,
          pageSize: 20,
          searchTerm: pickerSearch.trim() || undefined,
        });
        setPickerItems(result.items || []);
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar remitos pendientes'), {
          variant: 'error',
        });
      } finally {
        setPickerLoading(false);
      }
    };

    const t = window.setTimeout(() => void loadPicker(), 300);
    return () => window.clearTimeout(t);
  }, [enqueueSnackbar, pickerSearch, remitoId]);

  const lineasSeleccionadas = useMemo(
    () => lineas.filter((l) => l.selected && l.cantidadDevolver > 0),
    [lineas],
  );

  const handleCantidadChange = (nroItemRemito: number, value: string) => {
    const parsed = Number(value.replace(',', '.'));
    setLineas((prev) =>
      prev.map((l) => {
        if (l.nroItemRemito !== nroItemRemito) return l;
        if (!Number.isFinite(parsed) || parsed < 0) {
          return { ...l, cantidadDevolver: 0 };
        }
        return {
          ...l,
          cantidadDevolver: Math.min(parsed, l.pendienteDevolver),
          selected: parsed > 0,
        };
      }),
    );
  };

  const handleToggle = (nroItemRemito: number, checked: boolean) => {
    setLineas((prev) =>
      prev.map((l) =>
        l.nroItemRemito === nroItemRemito
          ? {
              ...l,
              selected: checked,
              cantidadDevolver: checked ? (l.cantidadDevolver > 0 ? l.cantidadDevolver : l.pendienteDevolver) : 0,
            }
          : l,
      ),
    );
  };

  const handleSave = async () => {
    if (!remito || !remitoId) return;
    if (lineasSeleccionadas.length === 0) {
      enqueueSnackbar('Seleccioná al menos una cantidad a devolver', { variant: 'warning' });
      return;
    }

    for (const l of lineasSeleccionadas) {
      if (l.cantidadDevolver > l.pendienteDevolver) {
        enqueueSnackbar(`La cantidad de ${l.codigoArticulo} supera el pendiente`, { variant: 'warning' });
        return;
      }
    }

    const loteErr = mensajeValidacionLotes(
      lineasSeleccionadas.map((l) => ({
        codigoArticulo: l.codigoArticulo,
        cantidad: l.cantidadDevolver,
        controlLoteSerie: l.controlLoteSerie,
        lotes: l.lotes,
      })),
    );
    if (loteErr) {
      enqueueSnackbar(loteErr, { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      const created = await ordenDevolucionService.createOrden({
        formulario: {
          nroCliente: remito.nroCliente || 0,
          nombre: remito.nombre,
          nroSuc: remito.nroSuc,
          nroLista: remito.nroLista,
          nroUbicacionOrigen: remito.nroUbicacionOrigen,
          nroFormularioRemito: remitoId,
          descripcion: descripcion || undefined,
        },
        items: lineasSeleccionadas.map((l) => ({
          codigoArticulo: (l.codigoArticulo || '').trim(),
          descripcion: l.descripcionArticulo,
          cantidad: l.cantidadDevolver,
          precioUnitario: Number(l.precioUnitario) || 0,
          nroFormularioRemito: remitoId,
          nroItemRemito: l.nroItemRemito,
          nroFormularioMaster: l.nroFormularioMaster,
          nroItemMaster: l.nroItemMaster,
          um: l.um,
          notas: l.notas,
          ...(l.lotes && l.lotes.length > 0 ? { lotes: l.lotes } : {}),
        })),
      });

      const nroDoc = formatNroDocumentoVenta(created.letra, created.nroSuc, created.nroComprobante);
      enqueueSnackbar(
        nroDoc ? `Orden de devolución ${nroDoc} creada` : 'Orden de devolución creada',
        { variant: 'success' },
      );
      navigate('/ventas/ordenes-devolucion');
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al crear la orden de devolución'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!remitoId) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Card sx={{ bgcolor: '#fff', borderRadius: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ventas/ordenes-devolucion')}>
                Volver
              </Button>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Nueva Orden de Devolución
              </Typography>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
              Seleccioná un remito con cantidades pendientes para generar la orden.
            </Alert>

            <TextField
              fullWidth
              placeholder="Buscar remito / cliente..."
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              sx={{ mb: 2 }}
            />

            {pickerLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Remito</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell align="right">Pendiente</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pickerItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No hay remitos pendientes
                        </TableCell>
                      </TableRow>
                    ) : (
                      pickerItems.map((p) => (
                        <TableRow key={p.nroFormularioRemito} hover>
                          <TableCell>#{p.nroFormularioRemito}</TableCell>
                          <TableCell>
                            {p.nroCliente} - {p.nombre || ''}
                          </TableCell>
                          <TableCell align="right">{formatCantidad(p.totalPendienteDevolver)}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                navigate(`/ventas/ordenes-devolucion/nuevo?remitoId=${p.nroFormularioRemito}`)
                              }
                              sx={{ textTransform: 'none' }}
                            >
                              Seleccionar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ventas/ordenes-devolucion')}>
                Volver
              </Button>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Nueva Orden de Devolución
              </Typography>
            </Stack>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving || lineasSeleccionadas.length === 0}
              onClick={() => void handleSave()}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Stack>

          {remito && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Devolviendo remito #{remito.nroFormulario} — Cliente {remito.nroCliente} {remito.nombre || ''}
              {remito.nroUbicacionOrigen != null ? ` — Depósito ${remito.nroUbicacionOrigen}` : ''}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Observaciones"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Artículo</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>UM</TableCell>
                  <TableCell align="right">Remitido</TableCell>
                  <TableCell align="right">Devuelto</TableCell>
                  <TableCell align="right">Pendiente</TableCell>
                  <TableCell align="right" width={140}>
                    A devolver
                  </TableCell>
                  <TableCell align="center" width={90}>
                    Lote
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Sin cantidades pendientes
                    </TableCell>
                  </TableRow>
                ) : (
                  lineas.map((l) => (
                    <TableRow key={l.nroItemRemito} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={l.selected}
                          onChange={(e) => handleToggle(l.nroItemRemito, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>{l.codigoArticulo}</TableCell>
                      <TableCell>{l.descripcionArticulo}</TableCell>
                      <TableCell>{l.um || '-'}</TableCell>
                      <TableCell align="right">{formatCantidad(l.cantidadRemitida)}</TableCell>
                      <TableCell align="right">{formatCantidad(l.cantidadDevuelta)}</TableCell>
                      <TableCell align="right">{formatCantidad(l.pendienteDevolver)}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={l.cantidadDevolver}
                          disabled={!l.selected}
                          inputProps={{ min: 0, max: l.pendienteDevolver, step: 'any' }}
                          onChange={(e) => handleCantidadChange(l.nroItemRemito, e.target.value)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <LoteLineaButton
                          controlLoteSerie={l.controlLoteSerie}
                          requiereVencimiento={l.requiereVencimiento}
                          codigoArticulo={l.codigoArticulo || ''}
                          cantidad={Number(l.cantidadDevolver) || 0}
                          nroUbicacion={Number(remito?.nroUbicacionOrigen) || 0}
                          mode="ingreso"
                          lotes={l.lotes}
                          onChange={(lotes) =>
                            setLineas((prev) =>
                              prev.map((x) =>
                                x.nroItemRemito === l.nroItemRemito ? { ...x, lotes } : x,
                              ),
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdenDevolucionForm;
